import { Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Section, Spinner } from "@/components/kit";
import { brl } from "@/lib/api";
import { useBiz, useBizGlobal } from "./shared";

const pct = (v) => (v == null ? "—" : `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`);
const ROWS = [
  ["receita_bruta", "Faturamento", brl, true], ["crescimento_mes_pct", "Crescimento no mês", pct, true], ["mc_pct", "Margem de contribuição", pct, true],
  ["ebitda", "EBITDA", brl, true], ["margem_ebitda_pct", "Margem EBITDA", pct, true], ["lucro_liquido", "Lucro líquido", brl, true],
  ["margem_liquida_pct", "Margem líquida", pct, true], ["ponto_equilibrio", "Ponto de equilíbrio", (v) => (v == null ? "—" : brl(v)), false],
  ["caixa_final", "Caixa (saldo final)", brl, true], ["patrimonio_liquido", "Patrimônio líquido", brl, true], ["renda_passiva", "Renda passiva", brl, true],
  ["dividas", "Dívidas", brl, false], ["divida_pl", "Dívida / PL", (v) => (v == null ? "—" : `${v.toLocaleString("pt-BR")}x`), false], ["alertas_altos", "Alertas graves", (v) => v, false],
];

const best = (rows, k, higher) => {
  const vals = rows.filter((r) => r.has_data && r[k] != null).map((r) => r[k]);
  if (vals.length < 2) { return null; }
  const b = higher ? Math.max(...vals) : Math.min(...vals);
  return vals.filter((v) => v === b).length === 1 ? b : null;
};

export function Compare() {
  const { month, compareOk } = useBiz();
  const { data } = useBizGlobal(compareOk ? `/biz/compare?month=${month}` : null);
  if (!compareOk) {
    return (
      <div className="card-p max-w-xl p-8" data-testid="biz-compare-upgrade">
        <Crown className="h-8 w-8 text-[#D4AF37]" />
        <h3 className="mt-3 text-xl font-semibold">Comparativo exclusivo do plano Premium</h3>
        <p className="mt-2 text-sm text-slate-400">Veja todas as suas empresas lado a lado nos nove indicadores e descubra qual está puxando o resultado para cima ou para baixo.</p>
        <Button asChild className="mt-5"><Link to="/app/assinatura">Ver planos</Link></Button>
      </div>
    );
  }
  if (!data) return <Spinner />;
  const rows = data.rows;
  return (
    <Section title={`Comparativo entre empresas · ${month.slice(5)}/${month.slice(0, 4)}`} testid="biz-compare">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm" data-testid="biz-compare-table">
          <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="py-2 pr-4">Indicador</th>
            {rows.map((r) => <th key={r.id} className="px-3 text-right">{r.name}{!r.has_data && <div className="text-[10px] normal-case text-amber-300">sem lançamentos no mês</div>}</th>)}</tr></thead>
          <tbody>{ROWS.map(([k, label, f, higher]) => {
            const b = best(rows, k, higher);
            return (
              <tr key={k} className="border-t border-[#1E3A6E]/50" data-testid={`biz-compare-row-${k}`}>
                <td className="py-2.5 pr-4 text-slate-300">{label}</td>
                {rows.map((r) => <td key={r.id} className={`num px-3 text-right ${b != null && r[k] === b && r.has_data ? "font-semibold text-emerald-300" : ""}`}>{f(r[k])}</td>)}
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-500">Em verde, o melhor resultado de cada indicador entre as empresas com lançamentos no mês. Os valores usam os mesmos cálculos da Visão Geral de cada empresa.</p>
    </Section>
  );
}
